import { useEffect, useRef, useState } from "react";
import { Check, Loader2, Search } from "lucide-react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { dealerService } from "@/services/DealerService";
import type { DealerSearchResult, SelectedDealer } from "@/types/dealer";

interface ChangeDealerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialDealer?: SelectedDealer | null;
  onConfirm: (dealer: SelectedDealer) => void;
}

function formatDealer(dealer: SelectedDealer | null | undefined): string {
  if (!dealer) return "Not set";
  return [dealer.dealerName, dealer.postcode].filter(Boolean).join(", ");
}

export function ChangeDealerModal({ open, onOpenChange, initialDealer, onConfirm }: ChangeDealerModalProps) {
  const [dealer, setDealer] = useState<SelectedDealer | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [searchResults, setSearchResults] = useState<DealerSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const searchControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (open) setDealer(initialDealer ?? null);
  }, [open, initialDealer]);

  const reset = () => {
    searchControllerRef.current?.abort();
    searchControllerRef.current = null;
    setDealer(null);
    setSearchInput("");
    setSearchResults([]);
    setSearchLoading(false);
    setSearchError(null);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSearchDealers = async () => {
    const filter = searchInput.trim();
    if (filter.length < 3) {
      setSearchResults([]);
      setSearchError("Enter at least 3 characters to search.");
      return;
    }

    setSearchLoading(true);
    setSearchError(null);
    setSearchResults([]);
    searchControllerRef.current?.abort();
    const controller = new AbortController();
    searchControllerRef.current = controller;
    try {
      const result = await dealerService.searchDealers(filter, controller.signal);
      if (controller.signal.aborted) return;
      setSearchResults(result.searchResults);
      if (result.searchResults.length === 0) {
        setSearchError("No matching dealers found.");
      }
    } catch {
      if (!controller.signal.aborted) {
        setSearchError("Dealer search failed. Please try again.");
      }
    } finally {
      if (searchControllerRef.current === controller) {
        searchControllerRef.current = null;
        setSearchLoading(false);
      }
    }
  };

  const handleSelectDealer = (result: DealerSearchResult) => {
    setDealer({
      dealerId: result.dealerId,
      dealerName: result.dealerName,
      postcode: result.postcode ?? undefined,
      adminFee: result.adminFee,
    });
  };

  const handleConfirm = () => {
    if (!dealer) return;
    onConfirm(dealer);
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{initialDealer ? "Change Dealer" : "Add Dealer"}</DialogTitle>
          <DialogDescription>Search by dealer name, postcode or dealer ID.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              aria-label="Search dealers"
              className="pl-8"
              placeholder="Name, postcode or dealer ID"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void handleSearchDealers();
                }
              }}
            />
          </div>
          <Button
            aria-label="Search dealers"
            title="Search dealers"
            onClick={handleSearchDealers}
            disabled={searchLoading || searchInput.trim().length < 3}
          >
            {searchLoading ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
          </Button>
        </div>

        {searchError && (
          <p className="text-[12px] text-muted-foreground" role="status">
            {searchError}
          </p>
        )}

        {searchResults.length > 0 && (
          <div className="max-h-48 overflow-y-auto rounded-md border border-border" role="listbox" aria-label="Dealer search results">
            {searchResults.map((result) => (
              <button
                key={result.dealerId}
                type="button"
                role="option"
                aria-selected={dealer?.dealerId === result.dealerId}
                className="flex w-full items-center justify-between gap-3 border-b border-border px-3 py-2 text-left text-[12px] last:border-0 hover:bg-muted/60 aria-selected:bg-purple-50"
                onClick={() => handleSelectDealer(result)}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <Check
                    aria-hidden
                    className={`size-4 shrink-0 text-purple-600 ${dealer?.dealerId === result.dealerId ? "visible" : "invisible"}`}
                  />
                  <span className="min-w-0 truncate font-medium">{result.dealerName}</span>
                </span>
                <span className="shrink-0 text-muted-foreground">{result.postcode || `ID ${result.dealerId}`}</span>
              </button>
            ))}
          </div>
        )}

        {searchResults.length === 0 && (
          <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-muted/40 px-3 py-2">
            <span className="text-[12px] text-muted-foreground">Selected dealer</span>
            <span className="min-w-0 text-right text-[12px] font-medium text-foreground [overflow-wrap:anywhere]">
              {formatDealer(dealer)}
            </span>
          </div>
        )}

        <DialogFooter className="gap-3 sm:gap-3">
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            className="bg-purple-600 text-white hover:bg-purple-700"
            onClick={handleConfirm}
            disabled={!dealer}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
